import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { DualAxes } from '@ant-design/plots';
import { Card, Empty, Slider, Space } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CHART_COLORS } from '../../../constants/styles';
import { TrendsIntervalSize } from '../../../types/phlebotomist-dashboard';

// Color constants for this chart - using brand colors
const COLORS = {
  sglDonors: CHART_COLORS.successRates.sglDonors, // Warm beige - SGL Donors
  dblDonors: CHART_COLORS.successRates.dblDonors, // Royal blue - DBL Donors
  totalSuccess: CHART_COLORS.successRates.prbc, // Brand red - Total Success %
};

// Data point for the trend chart
export interface ITrendDataPoint {
  period: string; // e.g., "2024-01-01", "Week 1", "Jan 2024"
  singleDonors: number;
  doubleDonors: number;
  total: number;
  sglSuccessRate: number; // percentage 0-100
  dblSuccessRate: number; // percentage 0-100
  totalSuccessRate: number; // percentage 0-100
}

// Re-export with shorter alias for backward compatibility
export type TrendDataPoint = ITrendDataPoint;

interface IAttemptedDonationsTrendChartProps {
  data: TrendDataPoint[];
  loading?: boolean;
  intervalSize: TrendsIntervalSize;
  /** Container width from parent (for debugging/calculations) */
  containerWidth?: number;
  /** Pane width from parent (for debugging/calculations) */
  paneWidth?: number;
}

export const AttemptedDonationsTrendChart: FC<
  IAttemptedDonationsTrendChartProps
> = ({ data, loading = false, intervalSize, paneWidth }) => {
  const { t } = useTranslation('dashboard.PhlebotomistDashboard');

  // Labels for chart series
  const sglDonorsLabel = t('trends.sglDonors', 'SGL Donors');
  const dblDonorsLabel = t('trends.dblDonors', 'DBL Donors');
  const totalSuccessLabel = t('trends.totalSuccessRate', 'Total Success %');

  // Visibility state for each series
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

  // ============ ZOOM & SCALING CONSTANTS ============
  const BAR_MAX = 100; // Maximum bar width (px)
  const BAR_MIN = 13; // Minimum bar width (px) - at max zoom out
  const GAP_MIN = 8; // Minimum gap between bars (px) - at max zoom out
  const BAR_DEFAULT = 25; // Default bar width at zoom 4 (px)
  const GAP_DEFAULT = 15; // Default gap at zoom 4 (px)
  const CHART_PADDING = 200; // Padding for axes, labels, etc. (px)

  // Target pixels per data point (bar + gap) for readable density
  const TARGET_PX_PER_POINT = 25; // ~25px per bar is readable (similar to BAR_DEFAULT + GAP_MIN)

  // Calculate initial zoom level based on data point count
  // Goal: Start at a zoom level where bars are readable (not too squished)
  const initialZoom = useMemo(() => {
    const availableWidth = (paneWidth ?? 800) - CHART_PADDING;
    const dataPoints = data.length;

    if (dataPoints === 0) return 4;

    // Calculate what width we'd need at zoom=1 to have TARGET_PX_PER_POINT per bar
    const neededWidth = dataPoints * TARGET_PX_PER_POINT;

    // If the available width is already enough, use zoom 1
    if (neededWidth <= availableWidth) return 1;

    // Calculate the multiplier needed: neededWidth / availableWidth
    const neededMultiplier = neededWidth / availableWidth;

    // This chart uses a different scale (1-7 with 4 as center)
    // At zoom 1, bar+gap = BAR_MIN + GAP_MIN = 21px
    // At zoom 4, bar+gap = BAR_DEFAULT + GAP_DEFAULT = 40px
    // At zoom 7, bar+gap = BAR_MAX + gapMax = 100 + 60 = 160px
    // The scaling is exponential, so we need to reverse-engineer the zoom level
    // For simplicity, use log scale similar to other charts
    const calculatedZoom = 3 * Math.log2(neededMultiplier) + 1;

    // Clamp to valid range [1, 7] and round to nearest integer
    return Math.min(7, Math.max(1, Math.round(calculatedZoom)));
  }, [data.length, paneWidth]);

  // Zoom state: 1-7 scale, starts at calculated initial zoom
  const [zoom, setZoom] = useState<number | null>(null);
  const effectiveZoom = zoom ?? initialZoom;

  // Reset zoom when data length changes (new date range or interval selected)
  useEffect(() => {
    setZoom(null);
  }, [data.length, intervalSize]);

  // Calculate current scales based on zoom level (1-7)
  // Zoom 1 = barWidth = BAR_MIN (13px)
  // Zoom 4 = barWidth = BAR_DEFAULT (25px)
  // Zoom 7 = barWidth = BAR_MAX (100px)
  // Using exponential interpolation for smooth scaling
  const { barWidth, chartPages } = useMemo(() => {
    const availableWidth = (paneWidth ?? 800) - CHART_PADDING;

    // Calculate barWidth based on zoom level using exponential interpolation
    // zoom 1 -> BAR_MIN, zoom 4 -> BAR_DEFAULT, zoom 7 -> BAR_MAX
    let currentBarWidth: number;
    let currentGapWidth: number;

    if (effectiveZoom <= 4) {
      // Zoom 1-4: interpolate from BAR_MIN to BAR_DEFAULT
      // Using exponential: BAR_MIN * (BAR_DEFAULT/BAR_MIN)^((zoom-1)/3)
      const factor = (effectiveZoom - 1) / 3; // 0 to 1
      currentBarWidth = BAR_MIN * Math.pow(BAR_DEFAULT / BAR_MIN, factor);
      currentGapWidth = GAP_MIN * Math.pow(GAP_DEFAULT / GAP_MIN, factor);
    } else {
      // Zoom 5-7: interpolate from BAR_DEFAULT to BAR_MAX
      // Using exponential: BAR_DEFAULT * (BAR_MAX/BAR_DEFAULT)^((zoom-4)/3)
      const factor = (effectiveZoom - 4) / 3; // 0 to 1
      currentBarWidth = BAR_DEFAULT * Math.pow(BAR_MAX / BAR_DEFAULT, factor);
      // Gap continues to scale proportionally
      // At zoom 7, gap should be GAP_DEFAULT * (BAR_MAX/BAR_DEFAULT)
      const gapMax = GAP_DEFAULT * (BAR_MAX / BAR_DEFAULT);
      currentGapWidth = GAP_DEFAULT * Math.pow(gapMax / GAP_DEFAULT, factor);
    }

    const currentChartPages =
      ((currentBarWidth + currentGapWidth) * data.length) / availableWidth;

    return {
      barWidth: currentBarWidth,
      chartPages: currentChartPages,
    };
  }, [effectiveZoom, paneWidth, data.length]);

  // Chart width based on chartPages
  const chartWidth = useMemo(() => {
    if (chartPages <= 1) {
      return undefined; // Let chart auto-size
    }
    return ((paneWidth ?? 800) - CHART_PADDING) * chartPages;
  }, [chartPages, paneWidth]);

  // For DualAxes config, use calculated barWidth
  const barMaxWidth = Math.round(barWidth);

  const toggleSeries = (label: string) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  // Transform data for stacked bar chart (SGL + DBL donors)
  // Filter out hidden series
  const barData = useMemo(() => {
    const items: { period: string; value: number; type: string }[] = [];
    data.forEach((d) => {
      if (!hiddenSeries.has(sglDonorsLabel)) {
        items.push({
          period: d.period,
          value: d.singleDonors,
          type: sglDonorsLabel,
        });
      }
      if (!hiddenSeries.has(dblDonorsLabel)) {
        items.push({
          period: d.period,
          value: d.doubleDonors,
          type: dblDonorsLabel,
        });
      }
    });
    return items;
  }, [data, sglDonorsLabel, dblDonorsLabel, hiddenSeries]);

  // Transform data for line chart - Total Success % only
  // Return empty array if series is hidden
  const totalSuccessData = useMemo(() => {
    if (hiddenSeries.has(totalSuccessLabel)) return [];
    return data.map((d) => ({
      period: d.period,
      value: d.totalSuccessRate,
      type: totalSuccessLabel,
    }));
  }, [data, hiddenSeries, totalSuccessLabel]);

  // Performance: disable animations and heavy interactions for large datasets
  const isLargeDataset = data.length > 50;

  // Always use smooth curves for lines
  const lineShape = 'smooth';

  // @ant-design/plots v2 DualAxes config
  // Uses children array for mixed charts with shared x-axis
  const config = useMemo(
    () => ({
      xField: 'period',
      legend: false,
      // Disable animations for large datasets
      animate: !isLargeDataset,
      axis: {
        x: {
          label: {
            style: {
              fontWeight: (
                datum: { index: number },
                index: number
              ): number | string => (index % 2 === 0 ? 'bold' : 'normal'),
            },
          },
        },
      },
      children: [
        // Stacked bar chart for SGL + DBL donors
        {
          data: barData,
          type: 'interval',
          yField: 'value',
          colorField: 'type',
          stack: true,
          animate: !isLargeDataset,
          scale: {
            y: { key: 'donors' },
            color: {
              domain: [sglDonorsLabel, dblDonorsLabel],
              range: [COLORS.sglDonors, COLORS.dblDonors],
            },
          },
          style: {
            maxWidth: barMaxWidth,
            fill: ({ type }: { type: string }) => {
              if (type === sglDonorsLabel) return COLORS.sglDonors;
              if (type === dblDonorsLabel) return COLORS.dblDonors;
              return '#999';
            },
          },
          axis: {
            y: {
              position: 'left',
              title: t('trends.donorCount', 'Donor Count'),
            },
          },
          // Disable element highlight for large datasets (causes redraws)
          interaction: isLargeDataset
            ? { elementHighlight: false }
            : { elementHighlight: { background: true } },
        },
        // Line: Total Success % (deep navy)
        {
          data: totalSuccessData,
          type: 'line',
          yField: 'value',
          colorField: 'type',
          shapeField: lineShape,
          animate: !isLargeDataset,
          scale: {
            y: { key: 'rates', domain: [0, 100] },
            color: {
              // Order must match the order types are discovered (children order):
              // Bar: SGL Donors, DBL Donors, then line: Total Success
              range: [
                COLORS.sglDonors, // SGL Donors
                COLORS.dblDonors, // DBL Donors
                COLORS.totalSuccess, // Total Success %
              ],
            },
          },
          style: {
            stroke: COLORS.totalSuccess,
            lineWidth: 2,
          },
          axis: {
            y: {
              position: 'right',
              title: t('trends.successRate', 'Success Rate (%)'),
            },
          },
        },
      ],
    }),
    [
      barData,
      totalSuccessData,
      sglDonorsLabel,
      dblDonorsLabel,
      isLargeDataset,
      lineShape,
      barMaxWidth,
      t,
    ]
  );

  // Custom legend items (order: SGL Donors, DBL Donors, Total Success)
  const legendItems = [
    { label: sglDonorsLabel, color: COLORS.sglDonors },
    { label: dblDonorsLabel, color: COLORS.dblDonors },
    { label: totalSuccessLabel, color: COLORS.totalSuccess },
  ];

  const getIntervalLabel = () => {
    const labels: Record<TrendsIntervalSize, string> = {
      day: t('intervalSize.day', 'Day'),
      week: t('intervalSize.week', 'Week'),
      month: t('intervalSize.month', 'Month'),
      quarter: t('intervalSize.quarter', 'Quarter'),
      year: t('intervalSize.year', 'Year'),
    };
    return labels[intervalSize];
  };

  if (data.length === 0) {
    return (
      <Card
        title={t(
          'trends.donationCollectionTitle',
          'Donation Collection - Trends'
        )}
        loading={loading}
      >
        <Empty description={t('trends.noData', 'No data available')} />
      </Card>
    );
  }

  return (
    <Card
      title={`${t(
        'trends.donationCollectionTitle',
        'Donation Collection - Trends'
      )} (${t('trends.by', 'by')} ${getIntervalLabel()})`}
      loading={loading}
      style={{ position: 'relative' }}
    >
      {/* Custom Legend - clickable to toggle visibility */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 16,
          justifyContent: 'center',
        }}
      >
        {legendItems.map((item) => {
          const isHidden = hiddenSeries.has(item.label);
          return (
            <button
              key={item.label}
              type="button"
              onClick={() => toggleSeries(item.label)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 8px',
                border: '1px solid #d9d9d9',
                borderRadius: 4,
                background: isHidden ? '#f5f5f5' : '#fff',
                cursor: 'pointer',
                opacity: isHidden ? 0.5 : 1,
                transition: 'all 0.2s',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 12,
                  height: 12,
                  backgroundColor: isHidden ? '#d9d9d9' : item.color,
                  borderRadius: 2,
                  transition: 'background-color 0.2s',
                }}
              />
              <span
                style={{
                  fontSize: 12,
                  textDecoration: isHidden ? 'line-through' : 'none',
                  color: isHidden ? '#999' : '#333',
                }}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Zoom control */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          marginBottom: 8,
          gap: 8,
        }}
      >
        <span style={{ fontSize: 12, color: '#999' }}>
          {t('trends.zoomLabel', 'Zoom')}:
        </span>
        <Space size="small">
          <ZoomOutOutlined style={{ color: '#999' }} />
          <Slider
            min={1}
            max={7}
            step={1}
            value={effectiveZoom}
            onChange={setZoom}
            style={{ width: 120 }}
            tooltip={{
              formatter: (val) => `Level ${val}`,
            }}
          />
          <ZoomInOutlined style={{ color: '#999' }} />
        </Space>
      </div>

      {/* Scrollable chart container */}
      <div
        style={{
          overflowX: chartWidth ? 'auto' : 'hidden',
          overflowY: 'hidden',
        }}
      >
        <div
          style={{
            width: chartWidth,
            minWidth: chartWidth,
          }}
        >
          <DualAxes {...config} height={350} width={chartWidth} />
        </div>
      </div>
    </Card>
  );
};

export default AttemptedDonationsTrendChart;
